import React, { useState, useMemo, useEffect } from 'react';
import { useWeb3 } from '@/hooks/useWeb3';
import { useTeamDistribution } from '@/hooks/useTeamDistribution';
import { getClaimableAmountForGrant } from '@/hooks/getClaimableAmount';
import { usePublicClient } from 'wagmi';
import type { VestingGrant } from '@/hooks/useTeamDistribution';
import type { Address } from 'viem';
import { environment } from '@/config/environment';
import { GrantsList } from './GrantsList';
import { GrantDetails } from './GrantDetails';
import { GrantSchedule } from './GrantSchedule';
import { NotificationModal } from './NotificationModal';

export const UserDashboard: React.FC = () => {
  const web3 = useWeb3();
  const teamDist = useTeamDistribution();
  const publicClient = usePublicClient();
  const [selectedGrant, setSelectedGrant] = useState<VestingGrant | null>(null);
  const [selectedGrantClaimable, setSelectedGrantClaimable] = useState<bigint>(BigInt(0));
  const [isLoadingClaimable, setIsLoadingClaimable] = useState(false);
  const [contractTokenBalance, setContractTokenBalance] = useState<bigint>(BigInt(0));
  const [generalContractBalance, setGeneralContractBalance] = useState<bigint>(BigInt(0));
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // Fetch claimable amount from contract when grant is selected
  useEffect(() => {
    const fetchClaimableAmount = async () => {
      if (!selectedGrant || !publicClient || !web3.address) {
        setSelectedGrantClaimable(BigInt(0));
        return;
      }

      setIsLoadingClaimable(true);
      try {
        const contractAddress = environment.distributionAddress as Address;
        const claimableAmount = await getClaimableAmountForGrant(
          publicClient,
          contractAddress,
          web3.address as Address,
          selectedGrant.id
        );
        setSelectedGrantClaimable(claimableAmount);
      } catch (error) {
        console.error('Failed to fetch claimable amount:', error);
        setSelectedGrantClaimable(BigInt(0));
      } finally {
        setIsLoadingClaimable(false);
      }
    };

    fetchClaimableAmount();
  }, [selectedGrant, publicClient, web3.address]);

  // Fetch contract token balance when grant is selected
  useEffect(() => {
    const fetchContractBalance = async () => {
      if (!selectedGrant || !publicClient || !web3.tokenAddress) {
        setContractTokenBalance(BigInt(0));
        return;
      }

      try {
        const balance = await publicClient.readContract({
          address: web3.tokenAddress as Address,
          abi: [{
            name: 'balanceOf',
            type: 'function',
            stateMutability: 'view',
            inputs: [{ name: 'account', type: 'address' }],
            outputs: [{ name: '', type: 'uint256' }],
          }],
          functionName: 'balanceOf',
          args: [environment.distributionAddress as Address],
        }) as bigint;
        setContractTokenBalance(balance);
      } catch (error) {
        console.error('Failed to fetch contract token balance:', error);
        setContractTokenBalance(BigInt(0));
      }
    };

    fetchContractBalance();
  }, [selectedGrant, publicClient, web3.tokenAddress]);

  // Fetch contract token balance for general claim button
  useEffect(() => {
    const fetchGeneralContractBalance = async () => {
      if (!publicClient || !web3.tokenAddress || !web3.address) {
        setGeneralContractBalance(BigInt(0));
        return;
      }

      try {
        const balance = await publicClient.readContract({
          address: web3.tokenAddress as Address,
          abi: [{
            name: 'balanceOf',
            type: 'function',
            stateMutability: 'view',
            inputs: [{ name: 'account', type: 'address' }],
            outputs: [{ name: '', type: 'uint256' }],
          }],
          functionName: 'balanceOf',
          args: [environment.distributionAddress as Address],
        }) as bigint;
        setGeneralContractBalance(balance);
      } catch (error) {
        console.error('Failed to fetch general contract token balance:', error);
        setGeneralContractBalance(BigInt(0));
      }
    };

    fetchGeneralContractBalance();

    // Refresh every 30 seconds
    const interval = setInterval(fetchGeneralContractBalance, 30000);
    return () => clearInterval(interval);
  }, [publicClient, web3.tokenAddress, web3.address, teamDist.totalReleasable]);

  // Computed values
  const totalTokens = useMemo(() => {
    return web3.formatTokenAmount(teamDist.totalAllocation);
  }, [teamDist.totalAllocation, web3.formatTokenAmount]);

  const claimedTokens = useMemo(() => {
    return web3.formatTokenAmount(teamDist.totalReleased);
  }, [teamDist.totalReleased, web3.formatTokenAmount]);

  const availableTokens = useMemo(() => {
    return web3.formatTokenAmount(teamDist.totalReleasable);
  }, [teamDist.totalReleasable, web3.formatTokenAmount]);

  const vestingProgress = useMemo(() => {
    if (teamDist.totalAllocation === BigInt(0)) return '0.000';
    const progress =
      teamDist.totalReleased > BigInt(0)
        ? Number((teamDist.totalReleased * BigInt(100000)) / teamDist.totalAllocation) / 1000
        : 0;
    return progress.toFixed(3);
  }, [teamDist.totalAllocation, teamDist.totalReleased]);

  const vestingSchedule = useMemo(() => {
    if (teamDist.grants.length === 0) return [];
    return teamDist.calculateVestingSchedule(teamDist.grants[0]);
  }, [teamDist.grants, teamDist.tgeDate, teamDist.durations]);

  const startDate = useMemo(() => {
    if (!teamDist.tgeDate || teamDist.tgeDate === BigInt(0)) return 'Not set';
    return new Date(Number(teamDist.tgeDate) * 1000).toLocaleDateString();
  }, [teamDist.tgeDate]);

  const cliffDate = useMemo(() => {
    if (!teamDist.tgeDate || !teamDist.durations || teamDist.tgeDate === BigInt(0))
      return 'Not set';
    const cliffTime = Number(teamDist.tgeDate) + Number(teamDist.durations.cliffDuration);
    return new Date(cliffTime * 1000).toLocaleDateString();
  }, [teamDist.tgeDate, teamDist.durations]);

  const endDate = useMemo(() => {
    if (!teamDist.tgeDate || !teamDist.durations || teamDist.tgeDate === BigInt(0))
      return 'Not set';
    const endTime =
      Number(teamDist.tgeDate) +
      Number(teamDist.durations.cliffDuration) +
      Number(teamDist.durations.vestingDuration);
    return new Date(endTime * 1000).toLocaleDateString();
  }, [teamDist.tgeDate, teamDist.durations]);

  const canClaim = useMemo(() => {
    return teamDist.totalReleasable > BigInt(0);
  }, [teamDist.totalReleasable]);

  const hasGeneralInsufficientBalance = useMemo(() => {
    return teamDist.totalReleasable > BigInt(0) && generalContractBalance < teamDist.totalReleasable;
  }, [teamDist.totalReleasable, generalContractBalance]);

  const nextUnlock = useMemo(() => {
    const nextUnlock = vestingSchedule.find((s) => !s.unlocked);
    return nextUnlock;
  }, [vestingSchedule]);

  const selectedGrantVestingSchedule = useMemo(() => {
    if (!selectedGrant) return [];
    return teamDist.calculateVestingSchedule(selectedGrant);
  }, [selectedGrant, teamDist, teamDist.tgeDate, teamDist.durations]);

  const currentSelectedGrant = useMemo(() => {
    if (!selectedGrant) return null;
    return teamDist.grants.find((g) => g.id === selectedGrant.id) || selectedGrant;
  }, [selectedGrant, teamDist.grants]);

  useEffect(() => {
    if (!selectedGrant || !currentSelectedGrant) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedGrant(null);
      }
    };

    window.addEventListener('keydown', handleEsc);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedGrant, currentSelectedGrant]);

  const hasInsufficientBalance = useMemo(() => {
    return selectedGrantClaimable > BigInt(0) && contractTokenBalance < selectedGrantClaimable;
  }, [selectedGrantClaimable, contractTokenBalance]);

  const handleClaimTokens = async () => {
    try {
      await teamDist.releaseTokens();
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Tokens Claimed Successfully',
        message: 'Your tokens have been transferred to your wallet.',
      });
    } catch (error: any) {
      console.error('Failed to claim tokens:', error);

      // Check if the error is due to insufficient balance
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('Insufficient token balance in contract')) {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Insufficient Contract Balance',
          message: 'The distribution contract does not have enough tokens to fulfill your claim. Please contact the contract administrator to add more tokens.',
        });
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Claim Failed',
          message: errorMessage || 'Failed to claim tokens. Please try again.',
        });
      }
    }
  };

  const handleClaimFromGrant = async () => {
    if (!currentSelectedGrant) return;
    try {
      await teamDist.releaseFromGrant(currentSelectedGrant.id);
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Tokens Claimed Successfully',
        message: `Tokens from Grant #${currentSelectedGrant.id} have been transferred to your wallet.`,
      });
      setSelectedGrant(null); // Close the modal on success
    } catch (error: any) {
      console.error('Failed to claim tokens from grant:', error);

      // Check if the error is due to insufficient balance
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('Insufficient token balance in contract')) {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Insufficient Contract Balance',
          message: 'The distribution contract does not have enough tokens to fulfill your claim. Please contact the contract administrator to add more tokens.',
        });
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Claim Failed',
          message: errorMessage || 'Failed to claim tokens from grant. Please try again.',
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {teamDist.employmentInfo && teamDist.employmentInfo.isTerminated && (
        <div className="mb-6 bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 backdrop-blur-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <svg
                className="w-5 h-5 text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
              <p className="text-orange-200 text-sm">
                Employment terminated: vested tokens will continue to unlock according to TGE
                schedule.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-effect border border-primary/10 rounded-2xl p-6 hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Total Allocation</h3>
          <p className="text-3xl font-bold text-gradient-primary">{totalTokens}</p>
          {teamDist.grants.length > 1 && (
            <p className="text-xs text-gray-500 mt-1">{teamDist.grants.length} grants</p>
          )}
        </div>
        <div className="glass-effect border border-primary/10 rounded-2xl p-6 hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Total Claimed</h3>
          <p className="text-3xl font-bold text-gradient-primary">{claimedTokens}</p>
          <p className="text-xs text-gray-500 mt-1">{vestingProgress}% claimed</p>
        </div>
        <div className="glass-effect border border-primary/10 rounded-2xl p-6 hover:border-primary/20 transition-all duration-300 card-glow card-glow-hover">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Employment Status</h3>
          <p className="text-xl font-bold text-gradient-primary">
            {teamDist.employmentInfo?.isTerminated ? 'Terminated' : 'Active'}
          </p>
          {teamDist.employmentInfo && (
            <p className="text-xs text-gray-500 mt-1">
              Since{' '}
              {new Date(
                Number(teamDist.employmentInfo.employmentDate) * 1000
              ).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="glass-effect border border-primary/20 rounded-2xl p-6 card-glow">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Available to Claim</h3>
          <p className="text-3xl font-bold text-gradient-primary">{availableTokens}</p>
          {hasGeneralInsufficientBalance && (
            <div className="mt-3 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
              <p className="text-orange-200 text-xs font-semibold mb-1">⚠️ Insufficient Contract Balance</p>
              <p className="text-orange-300/80 text-xs">
                Contract has {web3.formatTokenAmount(generalContractBalance)} tokens.
                Contact admin to add more tokens.
              </p>
            </div>
          )}
          <button
            onClick={handleClaimTokens}
            disabled={!canClaim || teamDist.isLoading || hasGeneralInsufficientBalance}
            className="mt-4 w-full px-4 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30"
          >
            {teamDist.isLoading
              ? 'Processing...'
              : hasGeneralInsufficientBalance
              ? 'Insufficient Contract Balance'
              : canClaim
              ? 'Claim Tokens'
              : 'No Tokens Available'}
          </button>
        </div>
      </div>

      <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
        <h3 className="text-lg font-semibold text-gray-200 mb-4">Unlock Progress</h3>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <span className="text-xs font-semibold inline-block text-primary">
              {vestingProgress}% Claimed
            </span>
            <span className="text-xs font-semibold inline-block text-gray-500">
              {claimedTokens} / {totalTokens}
            </span>
          </div>
          <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-dark shadow-inner">
            <div
              style={{ width: `${vestingProgress}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-dark justify-center bg-primary transition-all duration-500 rounded-full shadow-lg shadow-primary/50"
            ></div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div>
            <p className="text-xs text-gray-500">TGE Date</p>
            <p className="text-sm font-medium text-gray-300">{startDate}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Cliff Date (25%)</p>
            <p className="text-sm font-medium text-gray-300">{cliffDate}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">End Date</p>
            <p className="text-sm font-medium text-gray-300">{endDate}</p>
          </div>
        </div>
      </div>

      {/* Multiple Grants Display */}
      {teamDist.grants.length > 1 && (
        <GrantsList grants={teamDist.grants} onGrantSelect={setSelectedGrant} />
      )}

      {/* Grant Details Modal */}
      {selectedGrant && currentSelectedGrant && (
        <div className="fixed inset-0 bg-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-primary/20 rounded-2xl p-6 max-w-6xl w-auto max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6 flex-none">
              <h3 className="text-2xl font-bold text-gradient-primary">
                Grant #{currentSelectedGrant.id.toString()} Details
              </h3>
              <button
                onClick={() => setSelectedGrant(null)}
                className="text-gray-400 hover:text-gray-200 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-6 overflow-y-auto pr-2 flex-1">
              {/* Grant Info Summary */}
              <div className="grid grid-cols-6 gap-4">
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Total Amount</p>
                  <p className="text-gray-200 font-semibold">
                    {web3.formatTokenAmount(currentSelectedGrant.totalAmount)}
                  </p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Released Amount</p>
                  <p className="text-gray-200 font-semibold">
                    {web3.formatTokenAmount(currentSelectedGrant.releasedAmount)}
                  </p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Claimable Amount</p>
                  {isLoadingClaimable ? (
                    <p className="text-gray-400 font-semibold animate-pulse">Loading...</p>
                  ) : (
                    <p className="text-green-400 font-semibold">
                      {web3.formatTokenAmount(selectedGrantClaimable)}
                    </p>
                  )}
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Grant Date</p>
                  <p className="text-gray-200 font-semibold">
                    {new Date(Number(currentSelectedGrant.grantDate) * 1000).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Schedule Type</p>
                  <p className="text-gray-200 font-semibold">
                    {currentSelectedGrant.useCustomSchedule ? 'Custom' : 'Default'}
                  </p>
                </div>
                <div className="bg-dark-lighter/50 border border-primary/10 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Status</p>
                  <p
                    className={`font-semibold ${
                      currentSelectedGrant.isActive ? 'text-green-400' : 'text-gray-400'
                    }`}
                  >
                    {currentSelectedGrant.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>

              {/* Insufficient Balance Warning */}
              {hasInsufficientBalance && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      ></path>
                    </svg>
                    <div className="flex-1">
                      <p className="text-orange-200 text-sm font-semibold mb-1">Insufficient Contract Balance</p>
                      <p className="text-orange-300/80 text-xs">
                        The contract currently has {web3.formatTokenAmount(contractTokenBalance)} tokens,
                        but you're trying to claim {web3.formatTokenAmount(selectedGrantClaimable)} tokens.
                        Please contact the administrator to add more tokens to the contract.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Claim Button */}
              <button
                onClick={handleClaimFromGrant}
                disabled={
                  isLoadingClaimable ||
                  !selectedGrantClaimable ||
                  selectedGrantClaimable === BigInt(0) ||
                  teamDist.isLoading ||
                  hasInsufficientBalance
                }
                className="w-full px-4 py-3 bg-primary hover:bg-primary-light text-dark font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30"
              >
                {isLoadingClaimable
                  ? 'Loading...'
                  : teamDist.isLoading
                  ? 'Processing...'
                  : hasInsufficientBalance
                  ? 'Insufficient Contract Balance'
                  : selectedGrantClaimable && selectedGrantClaimable > BigInt(0)
                  ? 'Claim Tokens from Grant'
                  : 'No Tokens Available to Claim'}
              </button>

              {/* Unlock Schedule */}
              <GrantSchedule
                grantId={currentSelectedGrant.id}
                schedule={selectedGrantVestingSchedule}
              />
            </div>
          </div>
        </div>
      )}

      {/* Next Unlock and Schedule - only for single grant */}
      {teamDist.grants.length === 1 && (
        <>
          <div className="glass-effect border border-primary/20 rounded-2xl p-6 card-glow">
            <h3 className="text-lg font-semibold text-gray-200 mb-2">Next Unlock</h3>
            <p className="text-4xl font-bold text-gradient-primary">
              {nextUnlock ? web3.formatTokenAmount(nextUnlock.amount) : '0'} tokens
            </p>
            <p className="text-sm text-gray-400 mt-2">
              on {nextUnlock ? nextUnlock.date.toLocaleDateString() : 'All tokens unlocked'}
            </p>
          </div>

          <div className="glass-effect border border-primary/10 rounded-2xl p-6 card-glow">
            <h3 className="text-lg font-semibold text-gray-200 mb-6">
              Unlock Schedule{' '}
              {teamDist.grants.length > 0 && `(Grant #${teamDist.grants[0].id})`}
            </h3>
            <GrantSchedule grantId={teamDist.grants[0].id} schedule={vestingSchedule} />
          </div>
        </>
      )}

      {/* Multiple Grants Notice */}
      {teamDist.grants.length > 1 && (
        <div className="glass-effect border border-primary/20 rounded-2xl p-6 card-glow">
          <h3 className="text-lg font-semibold text-gray-200 mb-4">📋 View Grant Details</h3>
          <p className="text-gray-400">
            Click on any grant above to view its complete unlock schedule and claim features. Each
            grant has its own vesting timeline.
          </p>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ ...notification, isOpen: false })}
      />
    </div>
  );
};
